import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx, createVNode as _createVNode } from "vue";
const _hoisted_1 = { class: "crm-contacts-page" };
const _hoisted_2 = { class: "page-header" };
const _hoisted_3 = { class: "page-title" };
const _hoisted_4 = { class: "entity-tag" };
const _hoisted_5 = {
    key: 0,
    class: "expand-caret"
};
const _hoisted_6 = { class: "count-chip" };
const _hoisted_7 = {
    key: 0,
    class: "complaint-badge"
};
const _hoisted_8 = {
    key: 1,
    class: "followed-up"
};
const _hoisted_9 = { key: 2 };
const _hoisted_10 = ["onClick"];
const _hoisted_11 = { class: "thread-contacts" };
const _hoisted_12 = ["onClick"];
const _hoisted_13 = { class: "thread-date" };
const _hoisted_14 = { class: "thread-method" };
const _hoisted_15 = { class: "thread-snippet" };
const _hoisted_16 = {
    key: 0,
    class: "complaint-badge"
};
const _hoisted_17 = { class: "drawer-header" };
const _hoisted_18 = { class: "drawer-content" };
const _hoisted_19 = { class: "record-heading" };
const _hoisted_20 = { class: "record-chips" };
const _hoisted_21 = {
    key: 0,
    class: "complaint-badge"
};
const _hoisted_22 = { class: "record-meta" };
const _hoisted_23 = { class: "meta-item" };
const _hoisted_24 = { class: "meta-value" };
const _hoisted_25 = { class: "meta-item" };
const _hoisted_26 = { class: "meta-value" };
const _hoisted_27 = { class: "meta-item" };
const _hoisted_28 = { class: "meta-value" };
const _hoisted_29 = { class: "meta-item" };
const _hoisted_30 = { class: "meta-value" };
const _hoisted_31 = {
    key: 0,
    class: "meta-item meta-wide"
};
const _hoisted_32 = { class: "meta-value" };
const _hoisted_33 = {
    key: 1,
    class: "followed-up"
};
const _hoisted_34 = { class: "record-section" };
const _hoisted_35 = { class: "record-body" };
const _hoisted_36 = {
    key: 0,
    class: "record-section"
};
const _hoisted_37 = { class: "record-body" };
const _hoisted_38 = { class: "reply-prompt-text" };
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import AdminTable from '@/admin/components/AdminTable.vue';
import MGDialog from '@/components/elements/MGDialog.vue';
import CrmContactDrawer from '@/admin/components/CrmContactDrawer.vue';
import { useAdmin } from '@/composables/useAdmin';
import { buildReplyContact, groupContactsIntoThreads, openFollowUp, threadStatus } from '@/composables/useAdminCrm';
const W = 'minmax(0, 1fr)';
export default /*@__PURE__*/ _defineComponent({
    __name: 'CrmContactsPage',
    setup(__props) {
        const route = useRoute();
        const admin = useAdmin();
        const entityType = computed(() => (route.params.entityType === 'employer' ? 'employer' : 'customer'));
        const entityId = computed(() => String(route.params.id || ''));
        const loading = ref(true);
        const entityName = ref('');
        const contacts = ref([]);
        const threads = ref([]);
        const methods = ref([]);
        const reasons = ref([]);
        const operators = ref([]);
        const showDrawer = ref(false);
        const drawerPrefill = ref(null);
        const showDetail = ref(false);
        const detailContact = ref(null);
        const showReplyPrompt = ref(false);
        const savedForReply = ref(null);
        const expandedRowId = ref(null);
        const columns = [
            { field: 'contactDateTime', label: 'Last Activity', sortable: true, width: W },
            { field: 'title', label: 'Title', width: 'minmax(0, 2.5fr)' },
            { field: 'count', label: 'Contacts', width: 'minmax(0, 0.5fr)' },
            { field: 'status', label: 'Status', sortable: true, width: 'minmax(0, 0.7fr)' },
            { field: 'reason', label: 'Reason', width: W },
            { field: 'followUpDue', label: 'Follow-up', width: W },
        ];
        // One row per thread: latest activity, thread title, contact count, latest reason,
        // complaint if any contact in the thread is one, and the thread's open follow-up
        const rows = computed(() => groupContactsIntoThreads(contacts.value).map((group) => {
            const latest = group.contacts[group.contacts.length - 1];
            const outstanding = openFollowUp(group.contacts);
            return {
                id: group.thread.id,
                contactDateTime: latest.contactDateTime,
                title: group.thread.title,
                count: group.contacts.length,
                status: threadStatus(group.contacts),
                reason: latest.contactReason?.name || '—',
                complaint: group.contacts.some(c => c.contactReason?.complaint),
                followUpDue: outstanding?.followUpDue || null,
                followedUp: group.contacts.some(c => c.followUpDue),
                thread: group.thread,
                contacts: group.contacts,
            };
        }).sort((a, b) => b.contactDateTime.localeCompare(a.contactDateTime)));
        // Operators are customers — resolve their names the same way CustomerNotesDrawer does
        function operatorName(id) {
            if (!id)
                return '—';
            const customer = (admin.adminState.customers || []).find((c) => c.id === id);
            if (customer?.person)
                return customer.person.email || `${customer.person.firstName} ${customer.person.lastName}`;
            return operators.value.find(o => o.id === id)?.name || '—';
        }
        const isOverdue = (date) => new Date(date) < new Date();
        // The open record's follow-up only counts as outstanding while it's the thread's newest contact
        const detailFollowUpOpen = computed(() => {
            const row = rows.value.find(r => r.contacts.some(c => c.id === detailContact.value?.id));
            return !!row && openFollowUp(row.contacts)?.id === detailContact.value?.id;
        });
        function statusLabel(status) {
            return status === 'open' ? 'Open' : status === 'pending' ? 'Awaiting reply' : 'Closed';
        }
        function formatDateTime(value) {
            if (!value)
                return '—';
            return new Date(value).toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
        }
        function formatDate(value) {
            if (!value)
                return '—';
            return new Date(value).toLocaleDateString('en-GB');
        }
        function loadEntityName() {
            if (entityType.value === 'employer') {
                admin.getEmployerDetails(entityId.value, (result) => {
                    entityName.value = result?.name || entityId.value;
                });
            }
            else {
                admin.getCustomerDetails(entityId.value, false, (result) => {
                    entityName.value = result?.person ? `${result.person.firstName} ${result.person.lastName}` : entityId.value;
                });
            }
        }
        function loadContacts(onLoaded) {
            admin.getCrmContacts(entityType.value, entityId.value, (result) => {
                contacts.value = result || [];
                loading.value = false;
                if (onLoaded)
                    onLoaded();
            });
            admin.getCrmThreads(entityType.value, entityId.value, (result) => {
                threads.value = result || [];
            });
        }
        function load() {
            loading.value = true;
            loadEntityName();
            loadContacts(() => {
                const deepLinkId = route.query.contactId;
                if (deepLinkId) {
                    const contact = contacts.value.find(c => c.id === deepLinkId);
                    if (contact) {
                        expandThreadOf(contact);
                        openDetail(contact);
                    }
                }
            });
        }
        function expandThreadOf(contact) {
            const row = rows.value.find(r => r.contacts.some(c => c.id === contact.id));
            if (row && row.count > 1)
                expandedRowId.value = row.id;
        }
        // Single-contact threads open straight into the record; longer threads expand in place
        function onRowSelect(row) {
            if (row.count === 1) {
                openDetail(row.contacts[0]);
                return;
            }
            expandedRowId.value = expandedRowId.value === row.id ? null : row.id;
        }
        function openNewContact() {
            drawerPrefill.value = null;
            showDrawer.value = true;
        }
        function addRelatedToThread(thread) {
            if (!thread)
                return;
            drawerPrefill.value = { thread };
            showDrawer.value = true;
        }
        function addRelatedFromDetail() {
            if (!detailContact.value)
                return;
            showDetail.value = false;
            addRelatedToThread(detailContact.value.thread);
        }
        function openDetail(contact) {
            if (!contact)
                return;
            detailContact.value = contact;
            showDetail.value = true;
        }
        function onContactSaved(created) {
            showDrawer.value = false;
            loadContacts(() => expandThreadOf(created));
            if (created.inbound && created.contactMethod && !created.contactMethod.verbal) {
                savedForReply.value = created;
                showReplyPrompt.value = true;
            }
        }
        function logReply() {
            if (!savedForReply.value)
                return;
            showReplyPrompt.value = false;
            drawerPrefill.value = buildReplyContact(savedForReply.value);
            showDrawer.value = true;
        }
        watch(() => [route.params.entityType, route.params.id], () => {
            if (route.params.id)
                load();
        });
        onMounted(() => {
            admin.getSelf();
            if (!admin.adminState.customers?.length)
                admin.getCustomers();
            admin.getCrmContactMethods((result) => { methods.value = result || []; });
            admin.getCrmContactReasons((result) => { reasons.value = result || []; });
            admin.getCrmOperators((result) => { operators.value = result || []; });
            load();
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("div", _hoisted_3, [
                        _createElementVNode("h1", null, _toDisplayString(entityName.value || '…'), 1),
                        _createElementVNode("span", _hoisted_4, _toDisplayString(entityType.value === 'employer' ? 'Employer CRM' : 'Member CRM'), 1)
                    ]),
                    _createElementVNode("button", {
                        class: "action-btn primary",
                        onClick: openNewContact
                    }, "+ New Contact")
                ]),
                _createVNode(AdminTable, {
                    title: "Contact History",
                    icon: "📇",
                    columns: columns,
                    data: rows.value,
                    loading: loading.value,
                    selectable: true,
                    "expanded-row-id": expandedRowId.value,
                    "empty-message": "No contacts logged yet",
                    onRowSelect: onRowSelect
                }, {
                    "cell-contactDateTime": _withCtx(({ row }) => [
                        _createTextVNode(_toDisplayString(formatDateTime(row.contactDateTime)), 1)
                    ]),
                    "cell-title": _withCtx(({ row }) => [
                        (row.count > 1)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(expandedRowId.value === row.id ? '▾' : '▸'), 1))
                            : _createCommentVNode("", true),
                        _createElementVNode("span", null, _toDisplayString(row.title || '(untitled)'), 1)
                    ]),
                    "cell-count": _withCtx(({ row }) => [
                        _createElementVNode("span", _hoisted_6, _toDisplayString(row.count), 1)
                    ]),
                    "cell-status": _withCtx(({ row }) => [
                        _createElementVNode("span", {
                            class: _normalizeClass(["status-chip", row.status])
                        }, _toDisplayString(statusLabel(row.status)), 3)
                    ]),
                    "cell-reason": _withCtx(({ row }) => [
                        _createElementVNode("span", null, _toDisplayString(row.reason), 1),
                        (row.complaint)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_7, "Complaint"))
                            : _createCommentVNode("", true)
                    ]),
                    "cell-followUpDue": _withCtx(({ row }) => [
                        (row.followUpDue)
                            ? (_openBlock(), _createElementBlock("span", {
                                key: 0,
                                class: _normalizeClass({ overdue: isOverdue(row.followUpDue) })
                            }, " Due " + _toDisplayString(formatDate(row.followUpDue)), 3))
                            : (row.followedUp)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_8, "Followed up"))
                                : (_openBlock(), _createElementBlock("span", _hoisted_9, "—"))
                    ]),
                    "custom-actions": _withCtx(({ row }) => [
                        _createElementVNode("button", {
                            class: "table-action-btn",
                            title: "Add further related contact",
                            onClick: _withModifiers(($event) => (addRelatedToThread(row.thread)), ["stop"])
                        }, "↩", 8, _hoisted_10)
                    ]),
                    "row-expansion": _withCtx(({ row }) => [
                        _createElementVNode("div", _hoisted_11, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row.contacts, (c) => {
                                return (_openBlock(), _createElementBlock("div", {
                                    key: c.id,
                                    class: "thread-contact",
                                    onClick: ($event) => (openDetail(c))
                                }, [
                                    _createElementVNode("span", _hoisted_13, _toDisplayString(formatDateTime(c.contactDateTime)), 1),
                                    _createElementVNode("span", {
                                        class: _normalizeClass(["direction-chip", c.inbound ? 'inbound' : 'outbound'])
                                    }, _toDisplayString(c.inbound ? '↓ In' : '↑ Out'), 3),
                                    _createElementVNode("span", _hoisted_14, _toDisplayString(c.contactMethod?.name), 1),
                                    _createElementVNode("span", _hoisted_15, _toDisplayString(c.body), 1),
                                    (c.contactReason?.complaint)
                                        ? (_openBlock(), _createElementBlock("span", _hoisted_16, "Complaint"))
                                        : _createCommentVNode("", true)
                                ], 8, _hoisted_12));
                            }), 128))
                        ])
                    ]),
                    _: 1
                }, 8, ["data", "loading", "expanded-row-id"]),
                (showDetail.value && detailContact.value)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: "drawer-overlay",
                        onClick: _cache[2] || (_cache[2] = ($event) => (showDetail.value = false))
                    }, [
                        _createElementVNode("div", {
                            class: "drawer detail-drawer",
                            onClick: _cache[1] || (_cache[1] = _withModifiers(() => { }, ["stop"]))
                        }, [
                            _createElementVNode("div", _hoisted_17, [
                                _cache[6] || (_cache[6] = _createElementVNode("h3", null, "Contact Record", -1)),
                                _createElementVNode("button", {
                                    class: "close-btn",
                                    onClick: _cache[0] || (_cache[0] = ($event) => (showDetail.value = false))
                                }, "✕")
                            ]),
                            _createElementVNode("div", _hoisted_18, [
                                _createElementVNode("div", _hoisted_19, [
                                    _createElementVNode("h4", null, _toDisplayString(detailContact.value.thread?.title || '(untitled)'), 1),
                                    _createElementVNode("div", _hoisted_20, [
                                        _createElementVNode("span", {
                                            class: _normalizeClass(["direction-chip", detailContact.value.inbound ? 'inbound' : 'outbound'])
                                        }, _toDisplayString(detailContact.value.inbound ? '↓ Inbound' : '↑ Outbound'), 3),
                                        (detailContact.value.contactReason?.complaint)
                                            ? (_openBlock(), _createElementBlock("span", _hoisted_21, "Complaint"))
                                            : _createCommentVNode("", true)
                                    ])
                                ]),
                                _createElementVNode("div", _hoisted_22, [
                                    _createElementVNode("div", _hoisted_23, [
                                        _cache[7] || (_cache[7] = _createElementVNode("span", { class: "meta-label" }, "Date & time", -1)),
                                        _createElementVNode("span", _hoisted_24, _toDisplayString(formatDateTime(detailContact.value.contactDateTime)), 1)
                                    ]),
                                    _createElementVNode("div", _hoisted_25, [
                                        _cache[8] || (_cache[8] = _createElementVNode("span", { class: "meta-label" }, "Method", -1)),
                                        _createElementVNode("span", _hoisted_26, _toDisplayString(detailContact.value.contactMethod?.name || '—'), 1)
                                    ]),
                                    _createElementVNode("div", _hoisted_27, [
                                        _cache[9] || (_cache[9] = _createElementVNode("span", { class: "meta-label" }, "Reason", -1)),
                                        _createElementVNode("span", _hoisted_28, _toDisplayString(detailContact.value.contactReason?.name || '—'), 1)
                                    ]),
                                    _createElementVNode("div", _hoisted_29, [
                                        _cache[10] || (_cache[10] = _createElementVNode("span", { class: "meta-label" }, "Logged by", -1)),
                                        _createElementVNode("span", _hoisted_30, _toDisplayString(operatorName(detailContact.value.operatorStaffId)), 1)
                                    ]),
                                    (detailContact.value.followUpDue)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_31, [
                                            _cache[11] || (_cache[11] = _createElementVNode("span", { class: "meta-label" }, "Follow-up", -1)),
                                            _createElementVNode("span", _hoisted_32, [
                                                (detailFollowUpOpen.value)
                                                    ? (_openBlock(), _createElementBlock("span", {
                                                        key: 0,
                                                        class: _normalizeClass({ overdue: isOverdue(detailContact.value.followUpDue) })
                                                    }, " Due " + _toDisplayString(formatDate(detailContact.value.followUpDue)) + " · " + _toDisplayString(operatorName(detailContact.value.followUpOperatorStaffId)), 3))
                                                    : (_openBlock(), _createElementBlock("span", _hoisted_33, "Followed up " + _toDisplayString(detailContact.value.followedUp ? formatDate(detailContact.value.followedUp) : ''), 1))
                                            ])
                                        ]))
                                        : _createCommentVNode("", true)
                                ]),
                                _createElementVNode("div", _hoisted_34, [
                                    _cache[12] || (_cache[12] = _createElementVNode("span", { class: "meta-label" }, "Body", -1)),
                                    _createElementVNode("div", _hoisted_35, _toDisplayString(detailContact.value.body), 1)
                                ]),
                                (detailContact.value.notes)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_36, [
                                        _cache[13] || (_cache[13] = _createElementVNode("span", { class: "meta-label" }, "Notes", -1)),
                                        _createElementVNode("div", _hoisted_37, _toDisplayString(detailContact.value.notes), 1)
                                    ]))
                                    : _createCommentVNode("", true)
                            ]),
                            _createElementVNode("div", { class: "drawer-footer" }, [
                                _createElementVNode("button", {
                                    class: "action-btn primary",
                                    onClick: addRelatedFromDetail
                                }, "Add further related contact")
                            ])
                        ])
                    ]))
                    : _createCommentVNode("", true),
                _createVNode(MGDialog, {
                    modelValue: showReplyPrompt.value,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((showReplyPrompt).value = $event)),
                    title: "Log your reply?",
                    "max-width": "440",
                    "content-height": "auto",
                    persistent: ""
                }, {
                    content: _withCtx(() => [
                        _createElementVNode("p", _hoisted_38, " This was an inbound " + _toDisplayString(savedForReply.value?.contactMethod ? savedForReply.value.contactMethod.name.toLowerCase() : 'contact') + ". Would you like to log the reply you sent? ", 1)
                    ]),
                    actions: _withCtx(() => [
                        _createElementVNode("button", {
                            class: "action-btn primary",
                            onClick: logReply
                        }, "Log reply"),
                        _createElementVNode("button", {
                            class: "action-btn secondary",
                            onClick: _cache[3] || (_cache[3] = ($event) => (showReplyPrompt.value = false))
                        }, "Not now")
                    ]),
                    _: 1
                }, 8, ["modelValue"]),
                _createVNode(CrmContactDrawer, {
                    show: showDrawer.value,
                    "entity-type": entityType.value,
                    "entity-id": entityId.value,
                    threads: threads.value,
                    methods: methods.value,
                    reasons: reasons.value,
                    operators: operators.value,
                    prefill: drawerPrefill.value,
                    onClose: _cache[5] || (_cache[5] = ($event) => (showDrawer.value = false)),
                    onSaved: onContactSaved
                }, null, 8, ["show", "entity-type", "entity-id", "threads", "methods", "reasons", "operators", "prefill"])
            ]));
        };
    }
});
